/**
 * Settled-outcome discrimination for tool parts.
 *
 * SSOT for the denied/error/result routing: every transcript consumer —
 * the record envelope's result slot, dialogue `transcript` overrides,
 * and the turn-state hooks (`useCompact`) — dispatches on the one value
 * derived here, and the denial-reason default lives here and nowhere
 * else.
 *
 * Typed over a structural part slice (the `RevisionScanMessage` pattern)
 * so `lib/tool` stays decoupled from the transport's concrete
 * `ToolUIPart` union; live parts satisfy the slice structurally.
 */

import { ProtocolError } from '#plugin-chat/lib/tool/errors/environment.ts';
import type { ToolOutputError } from '#plugin-chat/lib/tool/output-types.ts';
import { parseResultEnvelope } from '#plugin-chat/lib/tool/result-envelope.ts';

/** Structural slice of a tool UI part that outcome discrimination reads. */
export type SettledToolPart = {
  readonly state?: string;
  readonly output?: unknown;
  readonly errorText?: string;
  readonly approval?: { readonly reason?: string };
};

/** Tool part states carrying a settled record (success, failure, or denial). */
const TERMINAL_TOOL_STATES: ReadonlySet<string> = new Set(['output-available', 'output-error', 'output-denied']);

export const isTerminalToolPart = (part: SettledToolPart): boolean =>
  part.state !== undefined && TERMINAL_TOOL_STATES.has(part.state);

/**
 * Discriminated settled outcome of a terminal tool part. Derived per
 * part at render time and dispatched on by every outcome consumer.
 */
export type SettledOutcome =
  | { kind: 'denied'; reason: string }
  | { kind: 'error'; envelope: ToolOutputError }
  | { kind: 'result'; output: unknown };

/**
 * Structured failure for a *terminal* tool part, or `null` when the
 * part did not fail (success / denied / in-flight). A protocol error
 * (`output-error`, free text only) is synthesised into the same
 * {@link ToolOutputError} envelope as a tool-level structured failure,
 * so one `message.error` formatter and one compact failure line cover
 * every failure mode.
 */
export const terminalFailure = (part: SettledToolPart): ToolOutputError | null => {
  if (part.state === 'output-error') {
    return {
      success: false,
      errorType: ProtocolError.errorType,
      error: part.errorText ?? 'Tool execution failed.',
    };
  }
  if (part.state === 'output-available') {
    const result = parseResultEnvelope(part.output);
    return result.kind === 'success' ? null : result.error;
  }
  return null;
};

/**
 * Discriminated settled outcome for a tool part, or `null` while the
 * call is in flight.
 */
export const settledOutcome = (part: SettledToolPart): SettledOutcome | null => {
  if (part.state === 'output-denied') {
    return { kind: 'denied', reason: part.approval?.reason ?? 'Execution denied' };
  }
  const envelope = terminalFailure(part);
  if (envelope) {
    return { kind: 'error', envelope };
  }
  if (part.state === 'output-available') {
    return { kind: 'result', output: part.output };
  }
  return null;
};
